import { produce } from 'immer';
import { FieldSpecSchemaValues } from '@/zod/schemas/dataSource/Common';
import { DataSourceFieldSchemaValues } from '@/zod/schemas/dataSource/DataSourceField';
import { exhaustiveMatchingGuard } from '@/utils/util';
import { Datasource_FieldSpec } from '@/types/api';
import { FieldSpecTableRow } from '@/components/table/FieldSpecTable/FieldSpecTableData';

const getJoinTableDefinition = (join: Datasource_FieldSpec['join']) => {
  if (!join) {
    return null;
  }
  return `Join with "${join.withTable || join.withCSV?.path}"`;
};

export const getDefinition = (
  fieldSpec: FieldSpecSchemaValues | Datasource_FieldSpec,
) => {
  return (
    fieldSpec.from ||
    fieldSpec.alias ||
    fieldSpec.expr ||
    fieldSpec.literal ||
    getJoinTableDefinition(fieldSpec.join) ||
    ''
  );
};

export const getFieldSpecType = (
  fieldSpec?: Datasource_FieldSpec,
): DataSourceFieldSchemaValues['type'] => {
  if (fieldSpec?.literal) {
    return 'literal';
  }
  if (fieldSpec?.join?.withTable || fieldSpec?.join?.withCSV?.path) {
    return 'join';
  }
  if (fieldSpec?.expr) {
    return 'sql';
  }
  if (fieldSpec?.alias) {
    return 'alias';
  }
  return 'source';
};

export const mergeFieldSpecs = (
  fields?: Datasource_FieldSpec[],
  addFields?: Datasource_FieldSpec[],
  omitFields?: (string | undefined)[],
): Datasource_FieldSpec[] => {
  return [
    ...(fields ?? []).filter((q) => !omitFields?.includes(q.name || '')),
    ...(addFields ?? []),
  ];
};

export const getFieldTableRows = (
  existing?: Datasource_FieldSpec[],
  added?: FieldSpecSchemaValues[],
  omitFields?: (string | undefined)[],
): FieldSpecTableRow[] => {
  // get the existing fields, overwrite with the custom field if one of the same name exists
  const existingFields = (existing ?? []).map((field) => {
    const custom = (added ?? []).find(
      (addedField) => addedField.name === field.name,
    );
    return {
      id: `${field.name}-preset`,
      name: custom?.name || field.name || '',
      enabled:
        !!custom || !omitFields?.find((omitField) => omitField === field.name),
      type: getFieldSpecType(custom || field),
      definition: getDefinition(custom || field),
      source: {
        preset: field,
        custom,
      },
    };
  });

  // get the added fields, only add if they don't exist in the existing fields
  const addedFields = (added ?? [])
    .filter(
      (q) =>
        !existingFields.find((existingField) => existingField.name === q.name),
    )
    .map((field) => {
      return {
        id: `${field.name}-custom`,
        name: field.name || '',
        type: getFieldSpecType(field),
        definition: getDefinition(field),
        source: {
          custom: field,
        },
      };
    });

  return [...existingFields, ...addedFields];
};

export const getFieldSchemaValue = (
  field?: Datasource_FieldSpec,
): FieldSpecSchemaValues => {
  const values: FieldSpecSchemaValues = {
    name: field?.name ?? '',
    from: field?.from ?? '',
    alias: field?.alias ?? '',
    expr: field?.expr ?? '',
    literal: field?.literal ?? '',
    join: {
      withTable: field?.join?.withTable ?? '',
      lhs: field?.join?.lhs ?? '',
      rhs: field?.join?.rhs ?? '',
      withCSV: { path: field?.join?.withCSV?.path ?? '' },
      select: field?.join?.select ?? '',
      useCSV: !!field?.join?.withCSV?.path,
    },
    assert: field?.assert?.map(({ expr, message }) => ({
      expr: expr || '',
      message: message || '',
    })),
    comment: field?.comment ?? '',
  };

  return values;
};

export const getFieldSchemaValues = (
  fields: Datasource_FieldSpec[],
): FieldSpecSchemaValues[] => {
  return fields?.map((field) => getFieldSchemaValue(field)) ?? [];
};

export const updateFieldSchemaValue = (
  fieldSpec: Datasource_FieldSpec,
  customField?: FieldSpecSchemaValues,
  fields?: FieldSpecSchemaValues[],
) => {
  if (!fields) {
    throw new Error('Unable to update field on empty fields');
  }
  const index = fields.findIndex((f) => f === customField);
  if (index === -1) {
    throw new Error('Unable to update field, field not found');
  }
  fields.splice(index, 1, getFieldSchemaValue(fieldSpec));

  return [...fields];
};

export const deleteFieldSchemaValue = (
  fieldName: string,
  fields: FieldSpecSchemaValues[],
) => fields.filter((field) => field.name !== fieldName) ?? [];

export const addFieldSchemaValue = (
  fieldSpec: Datasource_FieldSpec,
  fields?: FieldSpecSchemaValues[],
) => {
  const field = getFieldSchemaValue(fieldSpec);
  return [...(fields || []), field];
};

export const setFieldSchemaValue = (fieldValue: FieldSpecSchemaValues) => {
  const join = fieldValue.join;
  const field: Datasource_FieldSpec = {
    name: fieldValue.name,
    from: fieldValue.from || undefined,
    alias: fieldValue.alias || undefined,
    expr: fieldValue.expr || undefined,
    literal: fieldValue.literal || undefined,
    join:
      join?.withTable || join?.withCSV?.path
        ? {
            withTable: join?.withTable || undefined,
            lhs: join?.lhs || undefined,
            rhs: join?.rhs || undefined,
            withCSV: join?.withCSV?.path
              ? { path: join.withCSV.path }
              : undefined,
            select: join?.select || undefined,
          }
        : undefined,
    assert: fieldValue.assert?.length ? fieldValue.assert : undefined,
    comment: fieldValue.comment || undefined,
  };

  return field;
};

export const setFieldSchemaValues = (values: FieldSpecSchemaValues[] = []) => {
  if (!values.length) {
    return undefined;
  }
  return values.map((fieldValue) => setFieldSchemaValue(fieldValue));
};

export const getConfigureFieldInitialValues = (
  fieldSpec: Datasource_FieldSpec,
  takenNames: string[],
): DataSourceFieldSchemaValues => {
  const name = fieldSpec.name || '';
  const from = fieldSpec.from || '';
  const alias = fieldSpec.alias || '';
  const literal = fieldSpec.literal || '';
  const expr = fieldSpec.expr || '';
  const join = fieldSpec.join || {};
  const comment = fieldSpec.comment || '';
  const withTable = fieldSpec.join?.withTable || '';
  const select = fieldSpec.join?.select || '';
  const lhs = fieldSpec.join?.lhs || '';
  const rhs = fieldSpec.join?.rhs || '';
  const path = fieldSpec.join?.withCSV?.path || '';
  const assert = (fieldSpec.assert || []).map((assertion) => ({
    expr: assertion.expr || undefined,
    message: assertion.message || undefined,
  }));

  let type: DataSourceFieldSchemaValues['type'] = 'source';
  if (expr) {
    type = 'sql';
  } else if (literal) {
    type = 'literal';
  } else if (join?.withTable || join?.withCSV?.path) {
    type = 'join';
  } else if (alias) {
    type = 'alias';
  }

  const values = {
    type,
    name,
    from,
    alias,
    literal,
    expr,
    join: { withTable, select, lhs, rhs, withCSV: { path }, useCSV: !!path },
    takenNames: takenNames.filter((taken) => taken !== name),
    comment,
    assert,
  };

  return values;
};

export const setConfigureFieldValues = (
  values: DataSourceFieldSchemaValues,
  fieldSpec: Datasource_FieldSpec = {},
): Datasource_FieldSpec => {
  const { name, type } = values;
  let from = undefined;
  let alias = undefined;
  let expr = undefined;
  let literal = undefined;
  let join = undefined;

  switch (type) {
    case 'source':
      from = values.from;
      break;
    case 'alias':
      alias = values.alias;
      break;
    case 'sql':
      expr = values.expr;
      break;
    case 'literal':
      literal = values.literal;
      break;
    case 'join':
      join = {
        withTable:
          !values.join?.useCSV && values.join?.withTable
            ? values.join?.withTable
            : undefined,
        lhs: values.join?.lhs || undefined,
        rhs: values.join?.rhs || undefined,
        withCSV:
          values.join?.useCSV && values.join?.withCSV?.path
            ? { path: values.join.withCSV.path }
            : undefined,
        select: values.join?.select || undefined,
      };
      break;
    default:
      exhaustiveMatchingGuard(type);
      break;
  }

  const fields = produce(fieldSpec, (draft) => {
    draft.name = name;
    draft.from = from;
    draft.alias = alias;
    draft.expr = expr;
    draft.literal = literal;
    draft.join = join;
    draft.assert = values.assert?.length ? values.assert : undefined;
    draft.comment = values.comment || undefined;
  });

  return fields;
};

export const getTakenNames = (fieldSpecData: FieldSpecTableRow[]) => {
  return fieldSpecData
    .filter((field) => !!field.name && field.enabled !== false)
    .map((field) => field.name || '');
};

export const getCopyableFields = (fieldSpecData: FieldSpecTableRow[]) => {
  return fieldSpecData
    .filter((field) => !!field.name)
    .map((field) => field.name || '')
    .filter((value, index, self) => self.indexOf(value) === index);
};
